import { type RefObject, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

/**
 * Obraz mieści się w oknie **bez przewijania**.
 *
 * Dotąd kadr miał `aspect-video w-full`, czyli wysokość wyliczaną z samej szerokości.
 * Aplikacja jest pełnoekranowa, więc na monitorze 4K szerokość treści to około 3700 px
 * — a to znaczy kadr wysokości ~2080 px przy 2160 px ekranu. Po dodaniu nagłówka,
 * wiersza tytułu, paska napisów i wiersza metadanych strona zawsze była wyższa niż
 * okno: dół obrazu wychodził poza ekran i trzeba było przewinąć, żeby sprawdzić, czy
 * kadr w ogóle jest cały. Na mniejszych ekranach to samo działo się w mniejszej skali.
 *
 * Prawidłowy rozmiar kadru wynika z **obu** wymiarów: szerokości okna i tego, ile
 * wysokości zostało po chromie. Drugiego nie da się wpisać liczbą — wiersz tytułu
 * bywa dwuliniowy, alert odliczania pojawia się i znika, pasek napisów rośnie po
 * wybraniu ścieżki, a tryb TV skaluje wszystko naraz. Dlatego **mierzymy**.
 *
 * Trzy własności pomiaru, każda z powodu:
 *
 * 1. **Górę liczymy względem dokumentu, nie okna.** `getBoundingClientRect().top`
 *    zmienia się przy przewijaniu, więc budżet liczony z niego rósłby po zjechaniu
 *    w dół i kadr puchłby przy każdym ruchu kółka.
 * 2. **To, co pod kadrem, mierzymy, a nie zgadujemy** — jako odstęp między dolną
 *    krawędzią kadru i dolną krawędzią całego bloku odtwarzacza. Ta odległość nie
 *    zależy od wysokości samego kadru, więc pomiar nie napędza sam siebie.
 * 3. **Zapas na dole bierzemy z dopełnienia `<main>`.** Na dotyku jest tam miejsce
 *    na pasek dolny razem z obszarem bezpiecznym telefonu; wpisanie tego liczbą
 *    rozjechałoby się przy pierwszej zmianie paska.
 */

/**
 * Poniżej tej wysokości nie schodzimy.
 *
 * W bardzo niskim oknie (podzielony ekran, klawiatura ekranowa telefonu) budżet
 * wychodzi absurdalnie mały. Wtedy lepiej pozwolić stronie się przewijać niż pokazać
 * pasek obrazu wysokości dwóch centymetrów.
 */
const MIN_HEIGHT_PX = 240;

export interface FitInput {
  /** Wysokość okna. */
  viewportHeight: number;
  /** Górna krawędź kadru względem DOKUMENTU, nie okna. */
  documentTop: number;
  /** Ile zajmuje to, co siedzi pod kadrem w tej samej kolumnie. */
  below: number;
  /** Zapas na dole strony — na dotyku miejsce na pasek dolny. */
  bottomReserve: number;
}

/**
 * Ile wysokości zostaje dla kadru.
 *
 * Wydzielone jako funkcja czysta, bo cała możliwa pomyłka jest tutaj — zgubiony
 * składnik albo znak daje kadr o kilkadziesiąt pikseli za wysoki, czyli dokładnie
 * ten objaw, który ta zmiana naprawia, tylko mniej widoczny.
 */
export function fitBudget({ viewportHeight, documentTop, below, bottomReserve }: FitInput): number {
  const available = viewportHeight - documentTop - below - bottomReserve;
  return Math.max(MIN_HEIGHT_PX, Math.floor(available));
}

/**
 * Ile korekt po fakcie wolno wykonać na jeden pomiar.
 *
 * Korekta zdejmuje resztkowe wystawanie strony, którego model układu nie przewidział.
 * Limit jest po to, żeby wystawanie z INNEGO powodu niż kadr nie zjechało obrazu do
 * minimum: po trzech próbach zostawiamy jak jest i strona się przewija.
 */
const MAX_CORRECTIONS = 3;

/**
 * Kadr po zdjęciu resztkowego wystawania.
 *
 * Osobna funkcja, bo to druga linia obrony i musi być sprawdzalna: pierwsza (model
 * układu) już raz się pomyliła — pominęła dopełnienie `<body>` w trybie TV i,
 * co gorsza, nie uruchamiała się wcale, dopóki kadr nie był w drzewie.
 */
export function shrinkByOverflow(budget: number, overflow: number): number {
  if (overflow <= 1) return budget;
  return Math.max(MIN_HEIGHT_PX, budget - Math.ceil(overflow));
}

export interface VideoFitParams {
  /** Kadr — element, którego wysokość ograniczamy. */
  containerRef: RefObject<HTMLElement | null>;
  /** Cały blok odtwarzacza, żeby zmierzyć, co siedzi pod kadrem. */
  rootRef: RefObject<HTMLElement | null>;
  /**
   * Czy w ogóle ograniczać.
   *
   * Na pełnym ekranie kadr ma wypełnić ekran i nie ma pod nim niczego, więc pomiar
   * byłby nie tylko zbędny, ale i błędny: `<main>` z jego dopełnieniem nie bierze
   * wtedy udziału w układzie.
   */
  enabled: boolean;
  /**
   * Czy kadr JEST już w drzewie.
   *
   * Bez tego pomiar nie następował nigdy i cała poprawka nie działała — a wyglądała
   * na działającą, bo kod był na miejscu. Kolejność jest taka: odtwarzacz montuje się
   * ze stanem `null` i pokazuje prostokąt zastępczy, więc przy pierwszym przebiegu
   * efektów **nie ma czego mierzyć** ani czego obserwować. Gdy materiał staje się
   * gotowy, kadr wchodzi do drzewa, ale tożsamość `measure` się nie zmienia, więc żaden
   * efekt nie rusza ponownie: budżet zostaje `null`, styl nie powstaje i kontener
   * wraca do wysokości liczonej z samej szerokości.
   *
   * Ta flaga jest jedynym sygnałem, po którym da się to poznać — `ref` nie powiadamia
   * o podłączeniu.
   */
  ready: boolean;
}

/** Wysokość w pikselach, jaką kadr może zająć, albo `null`, gdy nie ograniczamy. */
export function useVideoFit({
  containerRef,
  rootRef,
  enabled,
  ready,
}: VideoFitParams): number | null {
  const [budget, setBudget] = useState<number | null>(null);
  /** Licznik korekt w tym cyklu pomiaru — patrz `MAX_CORRECTIONS`. */
  const corrections = useRef(0);

  const measure = useCallback((): void => {
    corrections.current = 0;
    if (!enabled) {
      setBudget(null);
      return;
    }
    const container = containerRef.current;
    const root = rootRef.current;
    if (container === null || root === null) return;

    const frame = container.getBoundingClientRect();
    const block = root.getBoundingClientRect();
    // Względem dokumentu — patrz punkt 1 w opisie u góry.
    const documentTop = frame.top + window.scrollY;
    const below = Math.max(0, block.bottom - frame.bottom);

    /**
     * Zapas na dole: dopełnienie `<main>` **oraz** dopełnienie `<body>`.
     *
     * Pierwsze robi miejsce paskowi dolnemu z obszarem bezpiecznym telefonu. Drugie
     * istnieje tylko w trybie TV (`4svh` na overscan telewizora) i pominięcie go
     * wypychało kadr za dolną krawędź dokładnie o tę wartość — czyli w jedynym trybie,
     * w którym nie ma jak przewinąć pilotem.
     */
    const main = container.closest('main');
    const bottomReserve = paddingBottomOf(main) + paddingBottomOf(document.body);

    setBudget(fitBudget({ viewportHeight: window.innerHeight, documentTop, below, bottomReserve }));
  }, [containerRef, rootRef, enabled]);

  /**
   * Mierzymy też, gdy kadr **dopiero wchodzi** do drzewa.
   *
   * `ready` w zależnościach jest tu warunkiem działania całego mechanizmu, nie
   * optymalizacją — patrz opis pola w `VideoFitParams`.
   */
  useLayoutEffect(() => {
    // Warunek nie jest tu ostrożnością, a sygnałem: przed gotowością nie ma czego
    // mierzyć, a po niej trzeba zmierzyć NA NOWO.
    if (!ready) return;
    measure();
  }, [measure, ready]);

  useEffect(() => {
    if (!enabled || !ready) return;
    window.addEventListener('resize', measure);
    window.addEventListener('orientationchange', measure);

    /**
     * Obserwujemy blok odtwarzacza, bo jego wysokość zmienia się od rzeczy, których
     * nie zwiastuje żadne zdarzenie: pasek napisów po wybraniu ścieżki, wiersz
     * „wznowiono od…", alert odliczania. `resize` okna tego nie łapie.
     */
    const observer = new ResizeObserver(measure);
    const root = rootRef.current;
    if (root !== null) observer.observe(root);
    // Obserwujemy też sam kadr: gdy zmienia się jego szerokość (okno, tryb TV, rail
    // czatu), chcemy potwierdzić budżet bez czekania na zdarzenie okna.
    const container = containerRef.current;
    if (container !== null) observer.observe(container);

    return () => {
      window.removeEventListener('resize', measure);
      window.removeEventListener('orientationchange', measure);
      observer.disconnect();
    };
    // `ready` podpina obserwatora dopiero wtedy, gdy jest co obserwować — przy
    // pierwszym przebiegu oba `ref` są jeszcze puste.
  }, [measure, enabled, ready, rootRef, containerRef]);

  /**
   * Sprawdzenie SKUTKU, nie tylko modelu.
   *
   * Model liczy budżet z tego, co o układzie wiem. Ta pętla patrzy, czy strona po
   * zastosowaniu budżetu naprawdę mieści się w oknie, i jeśli nie — zdejmuje resztę.
   * Dzięki temu jedno przeoczone dopełnienie albo przyszły element w chromie nie
   * wracają jako ten sam błąd; wystawanie zdejmuje się samo.
   *
   * Działa po rysowaniu (zwykły `useEffect`), bo dopiero wtedy `scrollHeight` opisuje
   * układ z nowym rozmiarem kadru. Korekta wyłącznie w dół i najwyżej trzy razy.
   */
  useEffect(() => {
    if (budget === null || !enabled) return;
    if (corrections.current >= MAX_CORRECTIONS) return;

    const overflow = document.documentElement.scrollHeight - window.innerHeight;
    const next = shrinkByOverflow(budget, overflow);
    if (next === budget) return;

    corrections.current += 1;
    setBudget(next);
  }, [budget, enabled]);

  return budget;
}

/** Dolne dopełnienie elementu w pikselach; brak elementu to zero. */
function paddingBottomOf(element: Element | null | undefined): number {
  if (element === null || element === undefined) return 0;
  return Number.parseFloat(getComputedStyle(element).paddingBottom) || 0;
}

/**
 * Styl kadru z policzonego budżetu.
 *
 * Sama `max-height` nie wystarcza: kadr z proporcją 16:9 i szerokością `100%`
 * zachowałby pełną szerokość, a przycięta wysokość dałaby pudełko szersze niż obraz —
 * czyli czarne pasy po bokach i pasek sterowania wystający za kadr. Dlatego
 * ograniczamy **szerokość**, a proporcja sama wyznacza wysokość.
 */
export function videoFitStyle(budget: number | null): { width: string } | undefined {
  if (budget === null) return undefined;
  return { width: `min(100%, calc(${budget}px * 16 / 9))` };
}
